import { Injectable, Injector, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { InitialData } from 'app/app.types';
import { AuthUtils } from 'app/authentication/auth/auth.utils';
import { environment } from 'environments/environment';
import {
    buildModuleNavigationItems,
    normalizeModuleCode,
    resolveSingleModuleSurface,
    resolveTenantModuleCodes,
} from 'app/shared/entitlement/module-nav.config';
import { isModuleSectionActive } from 'app/shared/entitlement/module-nav-active.util';
import { DmsPinnedTemplatesService } from 'app/modules/dms/dms-pinned-templates.service';
import { isServicesBoHost, isTenantAppHost } from 'app/modules/services-store/services-host.util';
import { ProfileService } from 'app/modules/profile/profile.service';
import { TenantStoreApiService } from 'app/modules/store-bo/tenant-store-api.service';
import {
    isStoreModuleFlagsLoaded,
    isStoreModuleFlagsLoading,
    markStoreModuleFlagsLoadFailed,
    markStoreModuleFlagsLoading,
    resetStoreModuleFlagsCache,
    setStoreModuleFlags,
} from 'app/shared/entitlement/store-module-flags.state';

const EMPTY_NAV = {
    compact: [] as any[],
    default: [] as any[],
    futuristic: [] as any[],
    horizontal: [] as any[],
};

@Injectable({ providedIn: 'root' })
export class SessionNavigationService {
    private readonly _router = inject(Router);
    private readonly _injector = inject(Injector);
    private _storeApi: TenantStoreApiService | null = null;

    private _base: Omit<InitialData, 'navigation'> = {
        messages: [],
        notifications: [],
        shortcuts: [],
        user: null,
    };

    private _initialData$ = new BehaviorSubject<InitialData>({
        ...this._base,
        navigation: EMPTY_NAV,
    });

    readonly initialData$ = this._initialData$.asObservable();

    constructor(
        private _profileService: ProfileService,
        private _dmsPinnedTemplates: DmsPinnedTemplatesService,
    ) {
        this._profileService.selectedBranch$
            .subscribe(() => { resetStoreModuleFlagsCache(); this.refreshNavigation(); });

        this._profileService.selectedOrganization$
            .subscribe(() => { resetStoreModuleFlagsCache(); this.refreshNavigation(); });

        this._dmsPinnedTemplates.changes$.subscribe(() => this.refreshNavigation());

        this._router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
            this._syncModuleNavActiveStates();
        });
    }

    setBaseInitialData(initial: InitialData): void {
        this._base = {
            messages: initial.messages ?? [],
            notifications: initial.notifications ?? [],
            shortcuts: initial.shortcuts ?? [],
            user: initial.user ?? null,
        };
        this.refreshNavigation();
    }

    getInitialData(): InitialData {
        return this._initialData$.value;
    }

    refreshNavigation(): void {
        this._ensureStoreModuleFlagsLoaded();

        const user = this._readStoredUser();
        const sessionMenu = this._buildNavigationByRole(user?.role, user);

        this._initialData$.next({
            ...this._base,
            navigation: {
                compact: sessionMenu.compact || [],
                default: sessionMenu.default || [],
                futuristic: sessionMenu.futuristic || [],
                horizontal: sessionMenu.horizontal || [],
            },
        });
        this._syncModuleNavActiveStates();
    }

    /**
     * Load the tenant's `retail.modules.*` flags once (per org/branch) so
     * `canAccessStorePage()` can gate Reviews/Promo Codes/Newsletters nav items
     * and routes. Fetch is best-effort — `getSettings()` already resolves to
     * `null` for tenants without the store module or on request failure.
     *
     * Resolving `TenantStoreApiService` (which depends on `HttpClient`, whose
     * interceptor chain depends on `AuthService`) is deferred to a microtask.
     * `refreshNavigation()` can run synchronously from `AuthService`'s own
     * constructor chain (via `SessionNavigationService`'s constructor
     * subscriptions), and touching `HttpClient` on that same call stack would
     * re-enter `AuthService` while it is still being constructed (NG0200
     * circular dependency). Deferring lets that construction finish first.
     */
    private _ensureStoreModuleFlagsLoaded(): void {
        if (isStoreModuleFlagsLoaded() || isStoreModuleFlagsLoading()) {
            return;
        }
        markStoreModuleFlagsLoading();
        queueMicrotask(() => {
            if (!this._storeApi) {
                this._storeApi = this._injector.get(TenantStoreApiService);
            }
            this._storeApi.getSettings().subscribe({
                next: (payload) => {
                    const modules = (payload?.retail as { modules?: Record<string, boolean> } | undefined)?.modules ?? {};
                    setStoreModuleFlags({
                        bundling: modules['bundling'] !== false,
                        reviews: modules['reviews'] !== false,
                        promo_codes: modules['promo_codes'] !== false,
                        newsletters: modules['newsletters'] !== false,
                        affiliates: modules['affiliates'] !== false,
                    });
                    this.refreshNavigation();
                },
                error: () => markStoreModuleFlagsLoadFailed(),
            });
        });
    }

    private _readStoredUser(): any {
        const user = environment.user?.profile ?? null;
        if (!user) {
            return null;
        }

        return {
            ...user,
            role: AuthUtils.resolveEffectiveRoles(user?.extra?.role_map || user?.role, { user }),
        };
    }

    private _buildNavigationByRole(_role: any, user?: any): any {
        const isSuperAdmin = environment.user?.superadmin === true;
        const isAdmin = false;

        const superAdminMenu: any[] = [];

        const adminMenu = [
            { id: 'dashboard', title: 'Dashboard', type: 'basic', icon: 'heroicons_outline:chart-pie', link: '/dashboard' },
            { id: 'organizations', title: 'Organization', type: 'basic', icon: 'heroicons_outline:office-building', link: '/admin/organizations' },
            { id: 'branches', title: 'Branches', type: 'basic', icon: 'heroicons_outline:collection', link: '/admin/branches' },
            { id: 'users', title: 'Users', type: 'basic', icon: 'heroicons_outline:users', link: '/admin/users' },
        ];

        const branchModules = (environment.selection?.branch as { modules?: string[] } | undefined)?.modules ?? null;
        const moduleCodesForNav = isServicesBoHost()
            ? resolveTenantModuleCodes()
            : resolveTenantModuleCodes(branchModules);
        const moduleNav = buildModuleNavigationItems(moduleCodesForNav, this._dmsPinnedTemplates.list());

        // Single-module surface (e.g. Store-only / Outmailer-only): module-only nav.
        // Pulsify (`campaign`) and email Marketing keep dashboard + module menu.
        const singleModuleCode = resolveSingleModuleSurface(branchModules);
        const singleNormalized = singleModuleCode ? normalizeModuleCode(singleModuleCode) : null;
        if (singleNormalized && singleNormalized !== 'marketing' && singleNormalized !== 'campaign') {
            return this._wrapNavigation(this._buildSingleModuleMenu(moduleNav));
        }

        if (isServicesBoHost()) {
            const menu = this._buildTenantModuleNavigationMenu(moduleNav);
            return this._wrapNavigation(menu);
        }

        // Platform superadmin on tenant Outbox hosts still needs module sidebar (Store, etc.).
        if (isSuperAdmin && !isTenantAppHost()) {
            return this._wrapNavigation(superAdminMenu);
        }

        if (isAdmin) {
            return this._wrapNavigation(adminMenu);
        }

        return this._wrapNavigation(this._buildTenantModuleNavigationMenu(moduleNav));
    }

    /**
     * Single-module surface sidebar: the module's pages promoted to the top level,
     * so the whole admin reads as a focused single-purpose app (no Dashboard, no
     * module group header). A collapsable module block is flattened to its leaf
     * pages; the module home (exact link, e.g. `/store`) is matched exactly so it
     * doesn't stay highlighted on sub-pages.
     */
    private _buildSingleModuleMenu(moduleNav: any[]): any[] {
        const menu: any[] = [];

        for (const item of moduleNav) {
            if (item.type === 'collapsable' && item.children?.length) {
                const moduleHome = `${item.children.find((child: any) => child?.type === 'basic' && child?.link)?.link ?? ''}`.split('?')[0];
                item.children.forEach((child: any) => {
                    if (child.type === 'divider' || child.type === 'spacer') {
                        menu.push({ ...child });
                        return;
                    }
                    // Keep nested groups (e.g. a Settings sub-menu) intact and managed.
                    if (child.type === 'collapsable' && child.children?.length) {
                        menu.push(this._markNavActiveManaged(child));
                        return;
                    }
                    // Leaf page: let Fuse drive the active state from the router. The
                    // module home link is matched exactly so it isn't highlighted on
                    // every sub-page.
                    const link = `${child.link ?? ''}`.split('?')[0];
                    menu.push(link && link === moduleHome ? { ...child, exactMatch: true } : { ...child });
                });
                continue;
            }
            menu.push({ ...item });
        }

        return menu;
    }

    /** CC-style sidebar: Dashboard, divider, then one divider between each activated module. */
    private _buildTenantModuleNavigationMenu(moduleNav: any[]): any[] {
        const menu: any[] = [
            {
                id: 'dashboard',
                title: 'Dashboard',
                type: 'basic',
                icon: 'heroicons_outline:chart-pie',
                link: '/dashboard',
            },
        ];

        if (!moduleNav.length) {
            return menu;
        }

        menu.push({ id: 'nav-divider-main', type: 'divider' });

        moduleNav.forEach((item, index) => {
            if (index > 0) {
                menu.push({ id: `nav-divider-${item.id}`, type: 'divider' });
            }
            menu.push(this._markNavActiveManaged(item));
        });

        return menu;
    }

    private _markNavActiveManaged(item: any): any {
        if (item.type === 'divider' || item.type === 'spacer') {
            return { ...item };
        }

        if (item.type === 'collapsable' && item.children?.length) {
            return {
                ...item,
                navActiveManaged: true,
                children: item.children.map((child: any) => this._markNavActiveManaged(child)),
            };
        }

        return { ...item, navActiveManaged: true };
    }

    private _wrapNavigation(menu: any[]): any {
        return {
            compact: menu,
            default: menu,
            futuristic: menu,
            horizontal: menu,
        };
    }

    private _syncModuleNavActiveStates(): void {
        const current = this._initialData$.value;
        const url = this._router.url.split('?')[0];
        const menu = current.navigation.default;

        if (!Array.isArray(menu) || !menu.length) {
            return;
        }

        // Mutate in place so Fuse collapsable components keep expand state (trackBy id).
        let changed = false;
        for (const item of menu) {
            if (this._applyModuleNavActiveStatesInPlace(item, url)) {
                changed = true;
            }
        }

        if (!changed) {
            return;
        }

        this._initialData$.next({
            ...current,
            navigation: {
                compact: menu,
                default: menu,
                futuristic: menu,
                horizontal: menu,
            },
        });
    }

    /** @returns true when any `active` flag changed */
    private _applyModuleNavActiveStatesInPlace(item: any, url: string): boolean {
        if (item.type !== 'collapsable' || !item.children?.length) {
            return false;
        }

        let changed = false;
        let anyChildActive = false;

        for (const child of item.children) {
            if (child.type === 'collapsable' && child.children?.length) {
                if (this._applyModuleNavActiveStatesInPlace(child, url)) {
                    changed = true;
                }
                if (child.active || child.children?.some((c: any) => c.active)) {
                    anyChildActive = true;
                }
                continue;
            }

            const nextActive = child.navActiveManaged ? isModuleSectionActive(child, item, url) : !!child.active;
            if (child.active !== nextActive) {
                child.active = nextActive;
                changed = true;
            }
            if (nextActive) {
                anyChildActive = true;
            }
        }

        if (item.active !== anyChildActive) {
            item.active = anyChildActive;
            changed = true;
        }

        return changed;
    }
}
